/**
 * Format a leave balance/duration value with its translated unit label.
 * Supports: hours, days, weeks, months.
 * @param {number} value - The numeric value
 * @param {string} unit - The unit type ('hours', 'days', 'weeks', 'months')
 * @param {Function} t - Translation function from i18n
 * @returns {string} Formatted string like "5 days" or "18 weeks"
 */
export function formatLeaveValue(value, unit, t) {
  const u = (unit || 'days').toLowerCase();

  if (u === 'hours') {
    // Keep existing day+hour breakdown (8 hours = 1 day)
    const HOURS_PER_DAY = 8;
    // Clean up floating-point dust but preserve real fractions (e.g. 1.5h, 7.5h)
    const totalHours = Math.round(value * 100) / 100;
    const days = Math.floor(totalHours / HOURS_PER_DAY);
    const remainingHours = Math.round((totalHours - days * HOURS_PER_DAY) * 100) / 100;
    const parts = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? t('time.day') : t('time.days')}`);
    if (remainingHours > 0 || parts.length === 0) parts.push(`${remainingHours} ${remainingHours === 1 ? t('time.hour') : t('time.hours')}`);
    return parts.join(' ');
  }

  // Map unit to translation keys
  const unitKeys = {
    days: { one: 'time.day', other: 'time.days' },
    weeks: { one: 'time.week', other: 'time.weeks' },
    months: { one: 'time.month', other: 'time.months' },
  };

  const keys = unitKeys[u] || unitKeys.days;
  const label = value === 1 ? t(keys.one) : t(keys.other);
  return `${value} ${label}`;
}

/**
 * Get the unit label for display (no value).
 * @param {string} unit - The unit type
 * @param {Function} t - Translation function
 * @returns {string} Plural unit label like "days" or "weeks"
 */
export function getUnitLabel(unit, t) {
  const u = (unit || 'days').toLowerCase();
  const keyMap = {
    hours: 'time.hours',
    days: 'time.days',
    weeks: 'time.weeks',
    months: 'time.months',
  };
  return t(keyMap[u] || keyMap.days);
}
